import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Mail, 
  MessageSquare, 
  Settings, 
  TestTube, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Save,
  Loader
} from 'lucide-react';
import axios from 'axios';

interface NotificationTest {
  email: boolean;
  sms: boolean;
  timestamp: string;
}

interface NotificationSettings {
  emailEnabled: boolean;
  smsEnabled: boolean;
  criticalOnly: boolean;
  emailRecipients: string[];
  smsRecipients: string[];
  cooldownMinutes: number;
}

const NotificationSettings: React.FC = () => {
  const [settings, setSettings] = useState<NotificationSettings>({
    emailEnabled: false,
    smsEnabled: false,
    criticalOnly: true,
    emailRecipients: [],
    smsRecipients: [],
    cooldownMinutes: 30
  });
  
  const [testResults, setTestResults] = useState<NotificationTest | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Charger les paramètres depuis le localStorage ou l'API
      const saved = localStorage.getItem('notificationSettings');
      if (saved) {
        setSettings(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Sauvegarder dans le localStorage
      localStorage.setItem('notificationSettings', JSON.stringify(settings));
      
      // Ici vous pourriez aussi envoyer à l'API backend
      // await axios.post('/api/notifications/settings', settings);
      
      // Afficher un message de succès
      setTimeout(() => setSaving(false), 1000);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      setSaving(false);
    }
  };

  const testNotifications = async () => {
    setTesting(true);
    setTestResults(null);
    
    try {
      const response = await axios.post('/api/notifications/test');
      setTestResults(response.data.results);
    } catch (error) {
      console.error('Erreur test notifications:', error);
      setTestResults({
        email: false,
        sms: false,
        timestamp: new Date().toISOString()
      });
    } finally {
      setTesting(false);
    }
  };

  const addEmailRecipient = () => {
    if (newEmail && !settings.emailRecipients.includes(newEmail)) {
      setSettings(prev => ({
        ...prev,
        emailRecipients: [...prev.emailRecipients, newEmail]
      }));
      setNewEmail('');
    }
  };

  const removeEmailRecipient = (email: string) => {
    setSettings(prev => ({
      ...prev,
      emailRecipients: prev.emailRecipients.filter(e => e !== email)
    }));
  };

  const addPhoneRecipient = () => {
    if (newPhone && !settings.smsRecipients.includes(newPhone)) {
      setSettings(prev => ({
        ...prev,
        smsRecipients: [...prev.smsRecipients, newPhone]
      }));
      setNewPhone('');
    }
  };

  const removePhoneRecipient = (phone: string) => {
    setSettings(prev => ({
      ...prev,
      smsRecipients: prev.smsRecipients.filter(p => p !== phone)
    }));
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <Bell className="w-6 h-6 text-blue-600" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Configuration des Notifications
        </h2>
      </div>

      <div className="space-y-6">
        {/* Configuration générale */}
        <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Paramètres Généraux
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-600" />
                <span className="text-gray-900 dark:text-white">Notifications Email</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.emailEnabled}
                  onChange={(e) => setSettings(prev => ({ ...prev, emailEnabled: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-gray-600" />
                <span className="text-gray-900 dark:text-white">Notifications SMS</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.smsEnabled}
                  onChange={(e) => setSettings(prev => ({ ...prev, smsEnabled: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="text-gray-900 dark:text-white">Alertes Critiques Uniquement</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.criticalOnly}
                  onChange={(e) => setSettings(prev => ({ ...prev, criticalOnly: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-gray-900 dark:text-white">
                Délai entre alertes (minutes):
              </label>
              <input
                type="number"
                min="5"
                max="1440"
                value={settings.cooldownMinutes}
                onChange={(e) => setSettings(prev => ({ ...prev, cooldownMinutes: parseInt(e.target.value) }))}
                className="w-20 px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Configuration Email */}
        {settings.emailEnabled && (
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Destinataires Email
            </h3>
            
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="email@exemple.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  onKeyPress={(e) => e.key === 'Enter' && addEmailRecipient()}
                />
                <button
                  onClick={addEmailRecipient}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Ajouter
                </button>
              </div>
              
              <div className="space-y-2">
                {settings.emailRecipients.map((email, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                    <span className="text-gray-900 dark:text-white">{email}</span>
                    <button
                      onClick={() => removeEmailRecipient(email)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Configuration SMS */}
        {settings.smsEnabled && (
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Destinataires SMS
            </h3>
            
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="tel"
                  placeholder="+33123456789"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  onKeyPress={(e) => e.key === 'Enter' && addPhoneRecipient()}
                />
                <button
                  onClick={addPhoneRecipient}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Ajouter
                </button>
              </div>
              
              <div className="space-y-2">
                {settings.smsRecipients.map((phone, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                    <span className="text-gray-900 dark:text-white">{phone}</span>
                    <button
                      onClick={() => removePhoneRecipient(phone)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Test des notifications */}
        <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Test des Services
          </h3>
          
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={testNotifications}
              disabled={testing}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {testing ? <Loader className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
              {testing ? 'Test en cours...' : 'Tester les Notifications'}
            </button>
          </div>
          
          {testResults && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Résultats du Test:</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {testResults.email ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className="text-gray-900 dark:text-white">
                    Email: {testResults.email ? 'Succès' : 'Échec'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {testResults.sms ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className="text-gray-900 dark:text-white">
                    SMS: {testResults.sms ? 'Succès' : 'Échec'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Testé le: {new Date(testResults.timestamp).toLocaleString('fr-FR')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end gap-4">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
